/**
 * useSettleOnBoxes - when an Object's box changes, settle who stands on or in it (2026-10-26).
 *
 * A box is derived from the roster and the tokens, never written as terrain, so no map edit announces it: destroyed
 * to rubble or to nothing, moved, resized, it changes with a wound or a drag. Without this a marine on a destroyed
 * generator hangs 1.5 m up until the next map edit. The rule is the terrain's own (the user, 2026-10-26): a box no
 * higher than a step carries a figure up onto it, a taller one leaves him inside, flagged for the GM to drag free.
 *
 * What it will not do (the review):
 * - settle on the first sight of the map - the boxes seen once the terrain, roster and tokens have all arrived are
 *   the baseline, so a reload writes nothing;
 * - settle mid-drag - it waits for the boxes to hold still (`QUIET_MS`), so a crate dragged across a crowd is one
 *   settle at the end, not a write per pointer move;
 * - move anyone but the RIDERS of the boxes that changed (`ridersOf`) - a wound to a generator never drops a figure
 *   hovering across the map;
 * - stand anyone on a GM-hidden object's box (`settledBoxes` leaves those out).
 *
 * The GM's screen alone: every screen sees the change, and one writer is no race (as the tracker's object eviction).
 *
 * @param {object} args - isGm, terrain, roster, tokens (every token, the hidden ones included), hiddenIds, writeToken
 *   (routes a hidden figure to its record).
 */
import { useEffect, useMemo, useRef } from 'react';
import { objectDigest } from '../engine/objects/objectSolids';
import { ridersOf, settledBoxes, settleOnMap } from '../engine/objects/settleOnMap';
import { groundAtFor } from './useBattlefieldSync';
import useMirror from './useMirror';

/** How long the boxes must hold still before their riders are settled, ms: a drag ends first. */
export const QUIET_MS = 300;

const isReady = ({ terrain, roster, tokens }) => !!terrain && !!roster?.length && Object.keys(tokens ?? {}).length > 0;

/** Settle the riders of whatever changed between the boxes seen last and these; written at once. */
function settleRiders(before, boxes, { terrain, tokens, writeToken }) {
  const riders = ridersOf(before, boxes, tokens);
  if (!riders.length) return;
  const theirs = Object.fromEntries(riders.map((id) => [id, tokens[id]]));
  const { tokens: settled } = settleOnMap(theirs, terrain, null, groundAtFor(terrain), boxes);
  for (const token of Object.values(settled)) if (theirs[token.id] !== token) writeToken(token, { immediate: true });
}

/** The boxes as they stand now - rebuilt only when one would change (`objectDigest`), never for a walk elsewhere. */
function useBoxes(args) {
  const { terrain, roster, tokens, hiddenIds } = args;
  const ready = isReady(args);
  const digest = ready ? objectDigest({ roster, tokens, boundary: terrain.boundary, hiddenIds }) : null;
  const latest = useMirror(args);
  // Keyed on the terrain too: an edit under an object moves where its box stands.
  return useMemo(() => {
    if (digest === null) return null;
    const now = latest.current;
    return settledBoxes(
      terrain,
      { roster: now.roster, tokens: now.tokens, hiddenIds: now.hiddenIds },
      groundAtFor(terrain),
    );
  }, [digest, terrain, latest]);
}

export default function useSettleOnBoxes(args) {
  const { isGm } = args;
  const boxes = useBoxes(args);
  const latest = useMirror(args);
  const seen = useRef(null);
  useEffect(() => {
    if (!boxes) return undefined;
    if (!seen.current) {
      seen.current = boxes;
      return undefined;
    }
    const timer = setTimeout(() => {
      const before = seen.current;
      seen.current = boxes;
      if (isGm) settleRiders(before, boxes, latest.current);
    }, QUIET_MS);
    return () => clearTimeout(timer);
  }, [boxes, isGm, latest]);
}
